import { useEffect, useState } from "react";
import Carousel from "./Carousel";
import TwoByFiveGrid from "./masonry/TwoByFiveGrid";
import GallerySkeleton from "./GallerySkeleton";
import { useWidget } from "../../../providers/widget";
import { GalleryWidgetConfig } from "../config";
import { useFetchAllGalleryImages } from "../data";
import WidgetDataError from "../../../components/WidgetDataError";

export default function GalleryStyle() {
  const [isMobile, setIsMobile] = useState(false)
  const { options } = useWidget<GalleryWidgetConfig>()
  // The one place the gallery pages past the first 50 photos (#1810); the
  // other readers of the entry stay on useFetchGalleryImages.
  const { data, isLoading, error } = useFetchAllGalleryImages(options.filter)
  
  useEffect(() => {
    setIsMobile(window.innerWidth < 768)
    const handleBreakpointChange = () => {
      setIsMobile(window.innerWidth < 768)
    }
    window.addEventListener("resize", handleBreakpointChange)
    return () => {
      window.removeEventListener("resize", handleBreakpointChange)
    }
  }, [])

  // Show skeleton while loading
  if (isLoading) {
    return <GallerySkeleton />
  }

  const imageCount = data?.pages?.flatMap((page) => page.result || []).length ?? 0

  // Show a visible error when the images fetch failed (#740) — distinct from
  // the empty "no images" state below. Only when NOTHING loaded: a later page
  // failing (#1810) leaves the entry in error with its earlier pages intact,
  // and those photos stay on screen rather than giving way to this box.
  if (error && imageCount === 0) {
    return <WidgetDataError error={error} />
  }

  // Show empty state when there are no images
  if (imageCount === 0) {
    return (
      <div className={`homerunner-gallery-${options.style}-style-wrapper`}>
        <div className="homerunner-gallery-empty p-8 text-center">
          <div className="homerunner-gallery-empty-icon mx-auto h-12 w-12 text-muted-foreground mb-4" />
          <p className="homerunner-gallery-empty-text text-muted-foreground">No images available</p>
        </div>
      </div>
    )
  }

  // Simple style - always show carousel
  if (options.style === 'simple') {
    return (
      <div className="homerunner-gallery-simple-style-wrapper">
        <Carousel />
      </div>
    )
  }

  // Masonry style - responsive layout
  if (options.style === 'masonry') {
    return (
      <div className="homerunner-gallery-masonry-style-wrapper">
        {isMobile ? (
          <div className="homerunner-gallery-masonry-mobile">
            <Carousel />
          </div>
        ) : (
          <div className="homerunner-gallery-masonry-desktop">
            <TwoByFiveGrid />
          </div>
        )}
      </div>
    )
  }

  // Fallback to simple style
  return (
    <div className="homerunner-gallery-simple-style-wrapper">
      <Carousel />
    </div>
  )
}
